import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Lightbulb } from "lucide-react";
import { getArticle } from "@/data/articles";
import { ShareButtons } from "@/components/ShareButtons";
import { NewsletterBlock } from "@/components/NewsletterBlock";
import { useLang } from "@/lib/siteI18n";

const GOLD = "#C9A96E";
const BG = "#0A0A12";

const EASE = [0.16, 1, 0.3, 1];

const Fade = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[17px] md:text-lg leading-[1.9] text-white/75 font-light mb-6">{children}</p>
);

const SignalTitle = ({ children }) => (
  <h2 className="font-serif text-3xl md:text-4xl leading-tight mt-16 mb-6" style={{ color: GOLD }}>
    {children}
  </h2>
);

const ActionTip = ({ children }) => (
  <div
    className="my-8 p-6 md:p-7 border-l-2 rounded-r-sm"
    style={{ borderColor: GOLD, backgroundColor: "rgba(201,169,110,0.06)" }}
    data-testid="action-tip"
  >
    <div className="flex items-center gap-2 mb-3">
      <Lightbulb size={16} style={{ color: GOLD }} />
      <span className="text-xs uppercase tracking-luxe font-semibold" style={{ color: GOLD }}>
        Conseil d'action
      </span>
    </div>
    <p className="text-[16px] md:text-[17px] leading-[1.85] text-white/85 font-light">{children}</p>
  </div>
);

const BAGUA = [
  ["Nord", "Carrière, chemin de vie"],
  ["Nord-Est", "Connaissance, sagesse intérieure"],
  ["Est", "Famille, ancêtres, santé physique"],
  ["Sud-Est", "Prospérité, abondance"],
  ["Sud", "Réputation, rayonnement"],
  ["Sud-Ouest", "Amour, partenariat"],
  ["Ouest", "Créativité, projets, enfants"],
  ["Nord-Ouest", "Aide extérieure, mentors, voyages"],
  ["Centre", "Santé globale, équilibre"],
];

const COLORS = [
  [1, "Blanc, or, rouge vif", "Noir, gris foncé"],
  [2, "Argenté, vert pâle, bleu ciel", "Orange vif, rouge agressif"],
  [3, "Jaune, orange, turquoise", "Marron, gris"],
  [4, "Vert forêt, marron, terracotta", "Violet, rose vif"],
  [5, "Toutes les couleurs vives, multicolore", "Beige uniforme, monochrome"],
  [6, "Bleu royal, rose, vert émeraude", "Noir, gris métallique"],
  [7, "Violet, blanc pur, bleu profond", "Rouge, orange"],
  [8, "Noir, bordeaux, or", "Pastel, rose pâle"],
  [9, "Rouge, pourpre, doré", "Vert olive, kaki"],
];

export default function ArticlePage() {
  const { lang, t } = useLang();
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const setMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", value);
    };
    const url = `https://serenite-essence.fr/journal/${article.slug}`;
    const image = article.banner.startsWith("http") ? article.banner : `https://serenite-essence.fr${article.banner}`;
    document.title = `${article.title} | Journal Souverain, Académie AETHERIS`;
    setMeta("name", "description", article.excerpt);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:title", article.title);
    setMeta("property", "og:description", article.excerpt);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", image);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: BG }} data-testid="article-notfound">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-white mb-6">Article introuvable</h1>
          <Link to="/journal" className="uppercase tracking-luxe text-sm" style={{ color: GOLD }}>
            Retour au Journal
          </Link>
        </div>
      </div>
    );
  }

  // Nouveaux articles avec body structuré → rendu générique
  if (Array.isArray(article.body)) {
    return <StructuredArticle article={article} />;
  }


  return (
    <article className="min-h-screen" style={{ backgroundColor: BG }} data-testid="article-page">
      {/* HERO */}
      <div className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
        <motion.img
          src={article.banner}
          alt={article.bannerAlt || article.title}
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(to bottom, rgba(10,10,18,0.35) 0%, rgba(10,10,18,0.6) 55%, ${BG} 100%)` }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto w-full px-6 md:px-10 pb-12">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }}
              className="text-xs uppercase tracking-luxe mb-4" style={{ color: GOLD }}
            >
              {article.category}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 1 }}
              className="font-serif text-3xl md:text-5xl leading-[1.08]" style={{ color: GOLD }}
            >
              {article.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }}
              className="mt-5 text-white/60 text-sm md:text-base"
            >
              {article.date} · {article.author}{article.role ? ` · ${article.role}` : ""}
            </motion.p>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-3xl mx-auto px-6 md:px-10 pb-24 -mt-2">
        <Link to="/journal" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-12 mt-8">
          <ArrowLeft size={15} /> {t.article.back}
        </Link>
        {lang !== "fr" && (
          <p className="mb-10 text-sm italic text-white/50 border-l-2 pl-4" style={{ borderColor: GOLD }} data-testid="article-fr-only">{t.article.frOnly}</p>
        )}

        <Fade>
          <P>
            Il est 6h42. Vous ouvrez les yeux. Les pieds touchent le sol froid. Vous traversez le
            couloir, encore enveloppé de sommeil, et quelque chose vous effleure. Pas un bruit. Pas un
            courant d'air. Quelque chose de plus subtil. Une pesanteur dans le salon. Un malaise flottant
            devant cette étagère que vous ne regardez plus. Une lassitude qui n'a rien à voir avec la
            fatigue du corps.
          </P>
          <P>Vous mettez cela sur le compte du lundi. Ou du manque de café. Ou de la vie, tout simplement.</P>
          <P><em style={{ color: GOLD }}>Et pourtant.</em></P>
          <P>
            Et si votre espace vous parlait depuis des années ? Et si chaque matin, en traversant ces
            pièces familières, vous passiez à côté d'un message essentiel ? Le Feng Shui enseigne une
            vérité que la modernité a oubliée : l'espace n'est jamais neutre. Il respire. Il vibre. Il
            vous accompagne ou il vous freine. Aujourd'hui, Bâtisseur, nous allons apprendre ensemble à
            écouter. Cinq signaux. Cinq invitations silencieuses. Cinq clés de votre Réédification.
          </P>
        </Fade>

        {/* SIGNAL 1 */}
        <Fade>
          <SignalTitle>Signal 1. Le désordre chronique : quand le Chi ne peut plus circuler</SignalTitle>
          <P>
            En Feng Shui, le Chi désigne l'énergie vitale universelle. Imaginez-le comme un courant d'eau
            douce et puissante. Quand il circule librement dans votre habitat, il nourrit chaque domaine
            de votre existence : vos relations, votre santé, votre créativité, votre prospérité. Mais
            quand il rencontre un barrage, il stagne. Il se corrompt. Et les conséquences se manifestent
            dans votre vie quotidienne avec une précision troublante.
          </P>
          <P>
            Le désordre chronique est le premier barrage. Pas le désordre ponctuel d'un dimanche de
            rangement. Non. Celui qui revient toujours au même endroit. Celui que vous ne voyez plus.
          </P>
          <P>
            Pour comprendre pourquoi ce signal est si puissant, il faut connaître la grille du Bagua. Le
            Bagua est une carte énergétique composée de neuf secteurs, chacun associé à un domaine de vie :
            carrière (Nord), connaissance (Nord-Est), famille (Est), prospérité (Sud-Est), réputation
            (Sud), amour (Sud-Ouest), créativité (Ouest), aide extérieure (Nord-Ouest), et santé au centre.
          </P>
          <P>
            Prenons un exemple concret. Votre entrée est encombrée : chaussures empilées, courrier non
            ouvert, sacs abandonnés. En Feng Shui, l'entrée est la bouche du Chi. C'est par là que
            l'énergie pénètre votre habitat. Si cette bouche est obstruée, les opportunités ne peuvent
            tout simplement pas entrer. Vous vous demandez pourquoi les propositions professionnelles se
            tarissent, pourquoi les rencontres se font rares. Regardez votre entrée. Elle vous répond.
          </P>
          <ActionTip>
            Identifiez le domaine de vie qui vous préoccupe le plus en ce moment. Localisez le secteur
            correspondant sur le Bagua de votre habitat. Désencombrez cette zone en premier. Pas tout
            l'appartement. Juste cette zone. Et observez ce qui bouge dans les sept jours suivants.
          </ActionTip>
        </Fade>

        {/* SIGNAL 2 */}
        <Fade>
          <SignalTitle>Signal 2. Les zones d'ombre : là où le Sha Chi s'installe</SignalTitle>
          <P>
            Si le Chi est cette eau douce et nourricière, le Sha Chi en est l'opposé exact. C'est
            l'énergie qui descend, qui stagne, qui se décompose. On le trouve partout où la lumière ne
            pénètre plus, où l'air ne circule plus, où la vie a cessé de passer.
          </P>
          <P>
            Le Sha Chi adore l'obscurité. Et l'obscurité physique dans votre habitat reflète toujours une
            obscurité intérieure. Ce couloir que vous traversez à la hâte sans allumer. Cette pièce dont
            vous gardez la porte fermée. Ce recoin derrière le canapé que vous évitez du regard. Chaque
            zone d'ombre est un territoire non exploré de votre propre psyché.
          </P>
          <P>
            Voici un exemple que je rencontre régulièrement dans mes consultations. Un couple vit dans un
            bel appartement, lumineux, bien décoré. Mais il y a une chambre au fond du couloir. Celle de
            l'enfant parti. Ou celle du projet abandonné. La porte reste close. Les volets ne s'ouvrent
            plus. Le Sha Chi s'y accumule, semaine après semaine, et irradie silencieusement vers les
            pièces adjacentes. Le couple se plaint de fatigue inexpliquée. De tensions sans raison. La
            source est là, derrière cette porte fermée.
          </P>
          <P>
            L'obscurité physique nourrit les peurs inconscientes. Quand un espace reste dans l'ombre,
            votre subconscient l'interprète comme une menace latente. Un territoire non sécurisé. Même si
            votre mental rationalise, votre système nerveux, lui, reste en alerte.
          </P>
          <ActionTip>
            Ouvrez une porte fermée cette semaine. Pas pour transformer la pièce. Juste pour y laisser
            entrer la lumière. Ouvrez les volets. Allumez une bougie si la lumière naturelle manque.
            Placez-y une plante vivante. L'intention compte autant que le geste : vous dites au Sha Chi
            que ce territoire vous appartient à nouveau.
          </ActionTip>
        </Fade>

        {/* SIGNAL 3 */}
        <Fade>
          <SignalTitle>Signal 3. Les objets du passé : quand la mémoire vibratoire vous retient</SignalTitle>
          <P>
            Chaque objet que vous possédez porte une empreinte. Une vibration. Pas au sens ésotérique
            flou, mais au sens physique le plus concret : cet objet est associé à des souvenirs, des
            émotions, des périodes de votre vie. Chaque fois que votre regard le croise, même
            inconsciemment, votre cerveau réactive le circuit émotionnel correspondant.
          </P>
          <P>
            La numérologie nous enseigne que le Chemin de Vie est un mouvement. Un élan vers l'avant.
            Votre date de naissance dessine une trajectoire, et cette trajectoire exige du mouvement. Or,
            certains objets fonctionnent comme des ancres jetées dans le passé. Ils vous retiennent dans
            ce que vous étiez, au lieu de vous porter vers ce que vous devenez.
          </P>
          <P>
            Prenons un exemple délicat mais fréquent. Vous avez hérité d'un meuble. Ce meuble appartenait
            à un parent avec qui la relation était douloureuse. Vous l'avez gardé par culpabilité, par
            devoir, par cette petite voix qui dit « on ne jette pas un héritage ». Chaque matin, vous
            passez devant. Chaque matin, sans le savoir, vous réactivez cette douleur. Ce meuble n'est pas
            un souvenir. C'est une ancre énergétique.
          </P>
          <P>
            La différence est capitale. Un souvenir aimé vous élève quand vous le regardez. Vous souriez.
            Vous vous sentez plus grand. Une ancre énergétique, elle, vous tire vers le bas. Vous la
            contournez. Vous évitez d'y penser. Votre corps sait. Il a toujours su.
          </P>
          <ActionTip>
            Le rituel de libération avec gratitude. Prenez l'objet dans vos mains. Remerciez-le pour ce
            qu'il représentait. Reconnaissez la période de vie à laquelle il appartient. Puis dites, à
            voix haute : « Je te libère, et je me libère. » Donnez-le, vendez-le, ou recyclez-le. Le mode
            importe peu. C'est l'intention de passage qui libère le Chi.
          </ActionTip>
        </Fade>

        {/* SIGNAL 4 */}
        <Fade>
          <SignalTitle>Signal 4. Les coins morts : les secteurs oubliés du Bagua</SignalTitle>
          <P>
            Revenons à la grille du Bagua avec plus de précision. Superposez mentalement cette grille de
            neuf cases sur le plan de votre habitat, en alignant le bas de la grille avec votre porte
            d'entrée principale. Vous obtenez neuf secteurs :
          </P>
          <ul className="mb-8 space-y-3">
            {BAGUA.map(([dir, dom]) => (
              <li key={dir} className="flex gap-3 text-white/75 font-light leading-relaxed">
                <span className="shrink-0 font-semibold min-w-[110px]" style={{ color: GOLD }}>{dir}</span>
                <span>{dom}</span>
              </li>
            ))}
          </ul>
          <P>
            Un coin mort est un secteur de votre habitat où rien ne vit. Pas de lumière intentionnelle.
            Pas de plante. Pas d'objet signifiant. Juste un angle vide, peut-être un carton oublié, de la
            poussière. Ce vide n'est pas neutre : il correspond à un domaine de vie que vous avez
            inconsciemment mis en jachère.
          </P>
          <P>
            Imaginez un angle vide dans votre secteur Sud-Est. Pas de décoration. Pas de vie. Un simple
            recoin délaissé. En Feng Shui, le Sud-Est gouverne la prospérité et l'abondance. Ce coin mort
            est un message : vous avez cessé d'investir de l'énergie dans votre croissance financière.
            Peut-être par résignation. Peut-être par peur. L'espace le reflète avec une honnêteté
            désarmante.
          </P>
          <P>
            Si ce même vide se trouvait dans le secteur Sud-Ouest (amour), vous pourriez observer une
            solitude chronique ou une distance émotionnelle dans votre couple. Dans le secteur Est
            (santé), des douleurs récurrentes ou un épuisement sans cause apparente.
          </P>
          <ActionTip>
            Trois façons simples d'activer un coin mort. Premièrement, une plante vivante à feuilles
            rondes (symbole de croissance et d'abondance). Deuxièmement, un cristal de roche ou une
            améthyste, posé avec intention dans l'angle (le cristal capte et redistribue le Chi).
            Troisièmement, une source de lumière douce, une lampe d'ambiance ou une guirlande, allumée
            chaque soir pendant 21 jours consécutifs pour ancrer la nouvelle vibration. Choisissez
            l'activation qui vous parle. Votre intuition, ici, est votre meilleure alliée.
          </ActionTip>
        </Fade>

        {/* SIGNAL 5 */}
        <Fade>
          <SignalTitle>Signal 5. La couleur qui pèse : quand la vibration chromatique vous désaligne</SignalTitle>
          <P>
            Les couleurs ne sont pas de simples choix esthétiques. Chaque teinte émet une fréquence
            vibratoire mesurable. La chromothérapie et le Feng Shui s'accordent sur ce point : une
            couleur peut vous nourrir ou vous épuiser, selon votre propre fréquence personnelle.
          </P>
          <P>
            Et cette fréquence personnelle, la numérologie nous aide à la calculer. Votre Chemin de Vie se
            calcule en additionnant tous les chiffres de votre date de naissance jusqu'à obtenir un nombre
            entre 1 et 9. Exemple : né le 15 mars 1988. On additionne 1+5+0+3+1+9+8+8 = 35, puis 3+5 = 8.
            Chemin de Vie 8.
          </P>

          <div className="my-8 overflow-x-auto border border-white/10 rounded-sm" data-testid="colors-table">
            <table className="w-full text-left text-sm md:text-[15px]">
              <thead>
                <tr style={{ backgroundColor: "rgba(201,169,110,0.10)" }}>
                  <th className="p-4 font-serif text-base" style={{ color: GOLD }}>Chemin de Vie</th>
                  <th className="p-4 font-serif text-base" style={{ color: GOLD }}>Couleurs alignées</th>
                  <th className="p-4 font-serif text-base" style={{ color: GOLD }}>À éviter</th>
                </tr>
              </thead>
              <tbody>
                {COLORS.map(([n, ok, avoid], idx) => (
                  <tr key={n} className="border-t border-white/10" style={{ backgroundColor: idx % 2 ? "rgba(255,255,255,0.015)" : "transparent" }}>
                    <td className="p-4 font-semibold" style={{ color: GOLD }}>{n}</td>
                    <td className="p-4 text-white/80 font-light">{ok}</td>
                    <td className="p-4 text-white/45 font-light">{avoid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <P>
            Prenons un exemple. Vous êtes Chemin de Vie 7. Le 7 est le chercheur, l'introspectif, le
            spirituel. Sa fréquence vibre avec le violet et le blanc pur. Imaginez-vous vivre dans un
            salon entièrement rouge. Le rouge est feu, action, extériorité. Pour un 7, c'est une
            dissonance quotidienne. Un bruit de fond permanent qui épuise sans que vous puissiez
            identifier la source. Vous vous sentez agité, dispersé, incapable de vous concentrer chez vous.
          </P>
          <P>
            À l'inverse, un Chemin de Vie 1, le leader, le pionnier, se sentira renforcé par le blanc
            lumineux et l'or. Ces couleurs résonnent avec son énergie d'initiative et de clarté mentale.
          </P>
          <ActionTip>
            Vous n'avez pas besoin de repeindre vos murs. Commencez petit. Un coussin. Un tableau. Une
            nappe. Un plaid sur le canapé. Introduisez votre couleur alignée dans la pièce où vous passez
            le plus de temps. Retirez (ou couvrez) l'élément de couleur dissonante le plus visible. En
            deux semaines, observez votre niveau d'énergie dans cet espace.
          </ActionTip>
        </Fade>

        {/* CONCLUSION */}
        <Fade>
          <SignalTitle>Conclusion</SignalTitle>
          <P>
            Ces cinq signaux ne sont pas des condamnations. Ce ne sont pas des preuves que votre habitat
            est « mauvais » ou que vous avez échoué quelque part. Ce sont des invitations. Des messages
            patients que votre espace vous répète chaque matin, avec la constance d'un allié fidèle.
          </P>
          <P>
            Le concept de Réédification, au cœur de l'Académie AETHERIS, repose sur cette vérité : la
            transformation ne se fait pas en un jour, mais elle commence toujours par un premier geste
            conscient. Un tiroir vidé. Une lumière allumée. Un objet libéré. Un coin activé. Une couleur
            ajustée.
          </P>
          <P>
            Vous n'avez pas besoin de tout changer. Vous avez besoin de commencer. Un seul signal. Un
            seul geste. Aujourd'hui.
          </P>
          <P>
            Si l'un de ces cinq signaux vous a touché, si vous avez reconnu votre entrée encombrée, votre
            couloir sombre, ou cette couleur qui pèse sans que vous sachiez pourquoi, alors votre espace
            vous attend. Il est prêt pour sa Réédification. Et vous aussi.
          </P>
        </Fade>

        {/* CTA */}
        <Fade delay={0.1}>
          <div
            className="mt-14 p-8 md:p-10 text-center border rounded-sm"
            style={{ borderColor: "rgba(201,169,110,0.4)", backgroundColor: "rgba(201,169,110,0.05)" }}
          >
            <p className="font-serif text-2xl md:text-3xl mb-4" style={{ color: GOLD }}>
              Votre espace est votre premier temple.
            </p>
            <p className="text-white/70 font-light mb-8">
              Traitez-le en Bâtisseur souverain. Découvrez les thèmes d'harmonisation de l'Académie AETHERIS.
            </p>
            <Link
              to="/checkout/discovery_pack"
              data-testid="article-cta"
              className="inline-flex items-center gap-2 px-9 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: GOLD, color: BG }}
            >
              Réserver mon Diagnostic Gratuit <ArrowUpRight size={18} />
            </Link>
            <p className="mt-6 text-white/40 text-sm italic">
              {t.article.signature}
            </p>
          </div>

          <ShareButtons url={`${process.env.REACT_APP_BACKEND_URL}/api/share/journal/${article.slug}`} title={article.title} />
          <NewsletterBlock source={`journal:${article.slug}`} />
        </Fade>
      </div>
    </article>
  );
}

/* ============================================================
   Rendu générique pour les articles avec body structuré (blocs)
   ============================================================ */

function StructuredArticle({ article }) {
  const { lang, t } = useLang();
  const ctaHref = "/diagnostic-inaugural";
  const ctaFallback = "/diagnostic-inaugural";

  return (
    <article className="min-h-screen" style={{ backgroundColor: BG }} data-testid="article-page">
      {/* HERO */}
      <div className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
        <motion.img
          src={article.banner}
          alt={article.bannerAlt || article.title}
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = "/journal-banner.jpg"; }}
        />
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(to bottom, rgba(10,10,18,0.35) 0%, rgba(10,10,18,0.6) 55%, ${BG} 100%)` }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto w-full px-6 md:px-10 pb-12">
            {article.edition && (
              <motion.p
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.9 }}
                className="text-[11px] uppercase tracking-luxe mb-3 text-white/60"
              >
                {article.edition}
              </motion.p>
            )}
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }}
              className="text-xs uppercase tracking-luxe mb-4" style={{ color: GOLD }}
            >
              {article.category}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 1 }}
              className="font-serif text-3xl md:text-5xl leading-[1.08]" style={{ color: GOLD }}
            >
              {article.title}
            </motion.h1>
            {article.subtitle && (
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 1 }}
                className="mt-4 text-white/75 text-base md:text-lg italic font-light leading-relaxed max-w-2xl"
              >
                {article.subtitle}
              </motion.p>
            )}
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75, duration: 1 }}
              className="mt-5 text-white/60 text-sm md:text-base"
            >
              {article.date} · {article.author}{article.role ? ` · ${article.role}` : ""}
            </motion.p>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-3xl mx-auto px-6 md:px-10 pb-24 -mt-2">
        <Link to="/journal" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-12 mt-8">
          <ArrowLeft size={15} /> {t.article.back}
        </Link>
        {lang !== "fr" && (
          <p className="mb-10 text-sm italic text-white/50 border-l-2 pl-4" style={{ borderColor: GOLD }} data-testid="article-fr-only">{t.article.frOnly}</p>
        )}

        <Fade>
          {article.body.map((block, idx) => renderBlock(block, `${block.type}:${idx}:${(block.content || block.label || "").slice(0, 32)}`, ctaHref, ctaFallback))}
          <ShareButtons url={`${process.env.REACT_APP_BACKEND_URL}/api/share/journal/${article.slug}`} title={article.title} />
          <NewsletterBlock source={`journal:${article.slug}`} />
        </Fade>

        {/* CTA final commun à tous les articles structurés */}
        <Fade delay={0.05}>
          <div
            className="mt-16 p-8 md:p-10 border rounded-sm text-center"
            style={{ borderColor: "rgba(201,169,110,0.3)", backgroundColor: "rgba(201,169,110,0.04)" }}
          >
            <p className="text-xs uppercase tracking-luxe mb-4" style={{ color: GOLD }}>
              {t.article.actKicker}
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-white leading-snug mb-4">
              {t.article.actTitle} <span style={{ color: GOLD }}>{t.article.actAccent}</span>{t.article.actQ}
            </h3>
            <p className="text-white/60 leading-relaxed mb-8 max-w-xl mx-auto">
              {t.article.actText}
            </p>
            <Link
              to={ctaHref}
              data-testid="article-cta-bilan"
              className="inline-flex items-center gap-3 px-8 py-4 text-sm uppercase tracking-luxe transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: GOLD, color: BG }}
            >
              {t.article.actCta} <ArrowUpRight size={16} />
            </Link>
            <p className="mt-6 text-white/40 text-sm italic">
              {t.article.signature}
            </p>
          </div>
        </Fade>
      </div>
    </article>
  );
}

function renderBlock(block, blockKey, ctaHref, ctaFallback) {
  switch (block.type) {
    case "h2":
      return (
        <h2 key={blockKey} className="font-serif text-3xl md:text-4xl leading-tight mt-14 mb-6" style={{ color: GOLD }}>
          {block.content}
        </h2>
      );
    case "h3":
      return (
        <h3 key={blockKey} className="font-serif text-2xl leading-tight mt-10 mb-4 text-white">
          {block.content}
        </h3>
      );
    case "prologue":
      return (
        <p key={blockKey} className="text-[13px] uppercase tracking-luxe mt-2 mb-4 text-white/50">
          {block.content}
        </p>
      );
    case "quote":
      return (
        <blockquote
          key={blockKey}
          className="my-8 pl-6 md:pl-8 border-l-2"
          style={{ borderColor: GOLD }}
        >
          <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-white/85">
            « {block.content} »
          </p>
        </blockquote>
      );
    case "list":
      return (
        <ul key={blockKey} className="my-6 space-y-3">
          {(block.items || []).map((it) => (
            <li key={it} className="flex items-start gap-3 text-[17px] md:text-lg leading-[1.8] text-white/75 font-light">
              <span className="mt-2.5 shrink-0 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <aside key={blockKey} data-testid="article-callout" className="my-10 p-6 md:p-8 border rounded-sm"
          style={{ borderColor: "rgba(201,169,110,0.45)", backgroundColor: "rgba(201,169,110,0.06)" }}>
          <p className="text-xs uppercase tracking-luxe mb-3" style={{ color: GOLD }}>{block.label || "À savoir"}</p>
          <p className="text-[17px] md:text-lg leading-[1.8] text-white/85">{block.content}</p>
        </aside>
      );
    case "button":
      return (
        <div key={blockKey} className="my-10 text-center">
          <Link to={block.href || ctaFallback} data-testid="article-button-cta"
            className="inline-flex items-center gap-3 px-8 py-4 text-sm uppercase tracking-luxe transition-transform hover:scale-[1.03]"
            style={{ backgroundColor: GOLD, color: BG }}>
            {block.label} <ArrowUpRight size={16} />
          </Link>
        </div>
      );
    case "cta":
      // Le CTA visuel principal est ajouté globalement en bas — ce bloc reste discret.
      return (
        <p key={blockKey} className="mt-8 text-white/60 italic text-sm">
          → <a href={block.href ? block.href : ctaHref} className="underline hover:text-white transition-colors" style={{ color: GOLD }}>
            {block.label || "Obtenir mon Diagnostic Inaugural, Gratuit"}
          </a>
        </p>
      );
    case "p":
    default:
      return <P key={blockKey}>{block.content}</P>;
  }
}
